import { PropsWithChildren, ReactElement, useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { useCAWContext } from '../CAW';
import Calendar from './Calendar';
import DestinationPicker from './DestinationPicker';
import PromoCodeField from './PromoCodeField';
import RoomOptions from './RoomOptions';
import { translate, getCalendarCurrencyCode } from '@micro-apps/env';
import { AvailabilityStore } from '@micro-apps/stores';
import CAWMobileDialog from './CAWMobileDialog';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import CalendarMeta from './CalendarMeta';
import { IconEdit } from '../../icon';
import BevSafeText from '../../bev-safe-text';
import { getUpdatedTimeLabel } from '../../calendar';
import { useNightlyRatesCalendar } from '@fshr/experiments';

const displayUpdated = (date: string | undefined, locale?: Locale) => {
	const timeLabel = getUpdatedTimeLabel(date, translate, locale);
	if (!timeLabel) {
		return null;
	}

	return (
		<div className="w-full text-center">
			<span className="text-caption">{timeLabel}</span>
		</div>
	);
};

function EditButton({
	label,
	onClick,
	editable = true,
	children,
}: PropsWithChildren<{
	label?: string;
	onClick?: () => void;
	editable?: boolean;
}>): ReactElement<any> {
	return (
		<button
			className="Summary-field w-full flex flex-row justify-between items-center spacing-card-padding-y-s spacing-card-padding-x-xs border-b border-b-border-subtle spacing-button-inline-icon"
			disabled={!editable}
			onClick={editable ? onClick : undefined}
		>
			<div className="Summary-content flex flex-col justify-start items-start">
				{label && (
					<label className="text-label text-text-secondary Summary-label">
						<BevSafeText>{label}</BevSafeText>
					</label>
				)}
				{children}
			</div>
			{editable && (
				<div className="Summary-edit-button">
					<IconEdit width={20} height={20} />
				</div>
			)}
		</button>
	);
}

type Props = {
	step: number;
	startingStep: number;
	propertiesArray?: string[];
	setStep: (newStep: number) => void;
	isPrevStep?: boolean;
	destinationPlaceholder?: string;
	availabilityStore: AvailabilityStore;
	ppMode?: boolean;
	/** Mirrors the desktop `shouldHidePromoCode` gate so both layouts agree. */
	hidePromoCode?: boolean;
	submitSearch: () => void;
	updateSelection?: () => void;
};
export default function CAWMobileDialogContent({
	step,
	startingStep,
	propertiesArray = [],
	setStep,
	destinationPlaceholder,
	isPrevStep = false,
	availabilityStore,
	ppMode,
	hidePromoCode = false,
	submitSearch,
	updateSelection,
}: Props): ReactElement<any> {
	const { planStore, locale, dateFnsLocale } = useCAWContext();
	const { enabled: nightlyRatesEnabled } = useNightlyRatesCalendar();
	const [errorPromoCode, setErrorPromoCode] = useState(false);
	const allowEditDestination =
		startingStep === 1 || (propertiesArray.length === 0 && !!planStore?.search?.hotelCode);

	const lastUpdated = useMemo(
		() => planStore?.calendarResult?.lastUpdated,
		[planStore?.calendarResult]
	);

	useEffect(() => {
		if (availabilityStore.errorCode === 'E0009' || availabilityStore.errorCode === 'E0331') {
			setErrorPromoCode(true);
		}
	}, [availabilityStore.errorCode]);

	useEffect(() => {
		if (planStore?.search.promoCode === '') {
			setErrorPromoCode(false);
		}
	}, [planStore?.search.promoCode]);
	return (
		<>
			{step < 2 ? (
				<DestinationPicker
					step={step}
					setStep={setStep}
					propertiesArray={propertiesArray}
					destinationPlaceholder={destinationPlaceholder}
					updateSelection={updateSelection}
				/>
			) : undefined}

			{step === 2 ? (
				<>
					<div className="Calendar-container Calendar-mobile pt-[37px]">
						<div className="Inner-Dialog Calendar-container">
							<div className="Calendar-dialog">
								<div className="Calendar-row Calendar-row--active">
									<Calendar setupScrollEvent={!isPrevStep} />
								</div>
							</div>
						</div>
					</div>
					<div
						className={classnames(
							'absolute -bottom-[15px] w-[100vw] -left-[15px] bg-surface z-100 flex flex-col items-center justify-center spacing-card-padding-xs border-t border-t-border-subtle',
							{
								'spacing-stack-2xs': !nightlyRatesEnabled,
								'gap-y-[10px]': nightlyRatesEnabled,
							}
						)}
					>
						<CalendarMeta
							layout="mobile"
							nightlyRatesExperiment={nightlyRatesEnabled}
							currencyCode={
								nightlyRatesEnabled ? getCalendarCurrencyCode(planStore?.calendarResult) : undefined
							}
						/>
						{displayUpdated(lastUpdated, dateFnsLocale?.localeMappings[locale || 'en'])}
					</div>
				</>
			) : undefined}

			{step === 3 ? (
				<div className="Occupancy-container Occupancy-mobile">
					<div className="Occupancy-dialog OccupancyField_row">
						<div className="Dialog-content Occupancy-content Occupancy-wrapper">
							<div className="Dialog-content-content">
								<RoomOptions />
							</div>
						</div>
					</div>
				</div>
			) : undefined}

			{step === 4 ? (
				<div className="Summary-container Dialog-content">
					<CAWMobileDialog
						availabilityStore={availabilityStore}
						destinationPlaceholder={destinationPlaceholder}
						submitSearch={submitSearch}
						propertiesArray={propertiesArray}
						lockedStep={1}
						EditButton={({ open }) => (
							<EditButton
								label={translate('Destination')}
								onClick={open}
								editable={allowEditDestination}
							>
								<span className="text-subtitle2 text-text-primary">
									<BevSafeText>
										{planStore?.destination?.shortName || planStore?.destination?.officialName}
									</BevSafeText>
								</span>
							</EditButton>
						)}
					/>
					<CAWMobileDialog
						availabilityStore={availabilityStore}
						destinationPlaceholder={destinationPlaceholder}
						submitSearch={submitSearch}
						propertiesArray={propertiesArray}
						lockedStep={2}
						EditButton={({ open }) => (
							<EditButton
								label={translate('Dates')}
								onClick={() => {
									open?.();
									window.dispatchEvent(new Event('openDatesDialog'));
								}}
							>
								{/* prettier-ignore */}
								<span className="text-subtitle2 text-text-primary">
									{planStore?.search?.checkinDate && format(planStore?.search?.checkinDate, 'MMM d')}-
									{planStore?.search?.checkoutDate &&
										format(planStore?.search?.checkoutDate, 'd, yyyy')}
									{planStore?.search?.flexDatesSelected
										? ` | ± ${planStore?.search.flexDatesSelected} ${translate('days')}`
										: undefined}
								</span>
							</EditButton>
						)}
					/>
					<CAWMobileDialog
						availabilityStore={availabilityStore}
						destinationPlaceholder={destinationPlaceholder}
						submitSearch={submitSearch}
						propertiesArray={propertiesArray}
						lockedStep={3}
						EditButton={({ open }) => (
							<EditButton label={translate('Guests')} onClick={open}>
								{planStore?.search?.rooms?.map((room, i) => {
									return (
										<div key={i}>
											<span className="text-subtitle2 text-text-primary">{`${translate('Room {0}', [
												translate((i + 1).toString()),
											])} | ${room.adults} ${room.adults > 1 ? translate('Adults') : translate('Adult')}, ${room.children} ${room.children === 1 ? translate('Child') : translate('Children')}`}</span>
										</div>
									);
								})}
							</EditButton>
						)}
					/>
					{!ppMode && !hidePromoCode ? (
						<PromoCodeField variant="summary" errorPromoCode={errorPromoCode} />
					) : null}
				</div>
			) : undefined}
		</>
	);
}
